import * as React from 'react';
import { FileEdit, UserCheck, Video } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Button } from '@/components/forms/Button';
import { VisioPopupBlockedModal } from '@/components/common/modals/VisioPopupBlockedModal';
import {
  JOINABLE_SESSION_STATUSES,
  SESSION_TYPE_CONFIG,
} from '@/components/sessions/session-configs';
import {
  getAttendanceTooltip,
  getGroupAttendanceTooltip,
  getGroupCancelTooltip,
  getGroupEditTooltip,
  getGroupReportTooltip,
  getSessionCancelTooltip,
  getSessionEditTooltip,
  getSessionReportTooltip,
} from './session-table-utils';
import { useSessionItem, type SessionItemProps } from '@/hooks/useSessionItem';

/** Props for {@link SessionActions}. */
export interface SessionActionsProps extends Omit<SessionItemProps, 'pageActions'> {
  state: ReturnType<typeof useSessionItem>;
  stopPropagation?: boolean;
}

/** Row/card action cluster: view, edit, attendance, report, cancel, join. */
export const SessionActions: React.FC<SessionActionsProps> = ({
  session,
  group,
  onView,
  onEdit,
  onEditGroup,
  onCancel,
  onCancelGroup,
  onManageAttendance,
  state,
  stopPropagation,
}) => {
  const click = (fn: () => void) => (e?: React.MouseEvent) => {
    if (stopPropagation) e?.stopPropagation();
    fn();
  };

  if (group && state.groupDisplay) {
    const display = state.groupDisplay;
    const typeConfig = SESSION_TYPE_CONFIG[group.type];
    const openReport = () => {
      if (display.hasMultipleReports) {
        state.navigateToGroupReports(
          group.id,
          `${typeConfig?.label ?? group.type} · ${format(display.sessionDate, 'd MMM yyyy', {
            locale: fr,
          })}`,
        );
        return;
      }
      state.navigateToReport(display.firstActiveId);
    };

    return (
      <>
        {state.canJoinInformationMeeting &&
          group.type === 'information_meeting_in_person' &&
          JOINABLE_SESSION_STATUSES.has(display.firstActive?.status ?? '') && (
            <Button
              variant="outline"
              color="rgb(var(--color-error))"
              size="icon"
              loading={state.joinMeeting.isJoining}
              className={display.firstActive?.status === 'IN_PROGRESS' ? 'session-video-blink' : ''}
              onClick={click(() => void state.joinMeeting.join(display.firstActiveId))}
              icon={Video}
              tooltipText="Rejoindre réunion d'information"
            />
          )}
        <VisioPopupBlockedModal {...state.joinMeeting.popupBlocked} />
        <Button preset="row-view" onClick={click(() => onView?.(group.id))} />
        {state.canEdit && (
          <Button
            preset="row-edit"
            onClick={click(() =>
              onEditGroup ? onEditGroup(group) : onEdit?.(display.firstActiveId),
            )}
            disabled={state.editDisabled}
            tooltipText={getGroupEditTooltip(
              state.editDisabled,
              display.isTerminated,
              display.firstActiveId,
              state.isOutsidePastWindow,
              display.displayStatus,
            )}
          />
        )}
        {onManageAttendance && (
          <Button
            variant="ghost"
            color="#7c3aed"
            size="icon"
            onClick={click(() => onManageAttendance(display.firstActiveId))}
            icon={UserCheck}
            disabled={state.attendanceDisabled}
            tooltipText={getGroupAttendanceTooltip(state.attendanceDisabled)}
          />
        )}
        <Button
          variant="ghost"
          color="var(--color-3-nuance-1)"
          size="icon"
          onClick={click(openReport)}
          icon={FileEdit}
          disabled={state.reportDisabled}
          loading={state.isLoadingReport && !display.hasMultipleReports}
          tooltipText={getGroupReportTooltip(
            state.reportDisabled,
            group.status,
            display.hasMultipleReports,
          )}
        />
        {state.canCancel && (
          <Button
            preset="row-cancel"
            onClick={click(() => onCancelGroup?.(group.id, display.totalChildren))}
            disabled={state.cancelDisabled}
            tooltipText={getGroupCancelTooltip(state.cancelDisabled, group.status)}
          />
        )}
      </>
    );
  }

  if (!session || !state.sessionFlags) return null;
  const flags = state.sessionFlags;
  const showReport =
    (state.canViewReport && session.type !== 'information_meeting_in_person') ||
    (state.canViewReportInformation && session.type === 'information_meeting_in_person');

  return (
    <>
      {state.canJoinInformationMeeting && flags.showJoinMeeting && (
        <Button
          variant="outline"
          color="rgb(var(--color-error))"
          size="icon"
          loading={state.joinMeeting.isJoining}
          className={session.status === 'IN_PROGRESS' ? 'session-video-blink' : ''}
          onClick={click(() => void state.joinMeeting.join(session.id))}
          icon={Video}
          tooltipText="Rejoindre réunion d'information"
        />
      )}
      <VisioPopupBlockedModal {...state.joinMeeting.popupBlocked} />
      <Button preset="row-view" onClick={click(() => onView?.(session.id))} />
      {state.canEdit && (
        <Button
          preset="row-edit"
          onClick={click(() => onEdit?.(session.id))}
          disabled={state.editDisabled}
          tooltipText={getSessionEditTooltip(state.editDisabled, session.status)}
        />
      )}
      {onManageAttendance && (
        <Button
          variant="ghost"
          color="#7c3aed"
          size="icon"
          onClick={click(() => onManageAttendance(session.id))}
          icon={UserCheck}
          disabled={state.attendanceDisabled}
          tooltipText={getAttendanceTooltip(
            onManageAttendance,
            state.attendanceDisabled,
            session.status,
          )}
        />
      )}
      {showReport && (
        <Button
          variant="ghost"
          color="var(--color-3-nuance-1)"
          size="icon"
          onClick={click(() => state.navigateToReport(session.id))}
          icon={FileEdit}
          disabled={state.reportDisabled}
          loading={state.isLoadingReport}
          tooltipText={getSessionReportTooltip(state.isLoadingReport, session.status)}
        />
      )}
      {state.canCancel && (
        <Button
          preset="row-cancel"
          onClick={click(() => onCancel?.(session.id))}
          disabled={state.cancelDisabled}
          tooltipText={getSessionCancelTooltip(state.cancelDisabled, session.status)}
        />
      )}
    </>
  );
};
